import { PathDirection } from '../../src/types/DatastoreEnums'
import { enumerable } from './constructs/enumerableDecorator'
import { getAllEdgeDefinitionTypes } from '../classDependentTypes/EdgeTypes/edgeTypeMatrixFns'
import { CustomerAccountType, CustomerProps, ICustomer, UserAttributeDef, UserAttributeDefKeys, type UserAttributeDefs } from '../types/ModelTypes'
import { Organization } from './Organization'

const typeName = 'Customer'

const customerEdgeDefs = typeof getAllEdgeDefinitionTypes === 'function' ? getAllEdgeDefinitionTypes('Customer') : {}

const commonAttDefBase: UserAttributeDefKeys = {
  type: 'RelatedType',
  cardinality: {
    in: null,
    out: null,
  },
}

const commonAttDefsOut: UserAttributeDefKeys = {
  ...commonAttDefBase,
  direction: PathDirection.child,
  objectTypeId: typeName,
}

const commonAttDefsIn: UserAttributeDefKeys = {
  ...commonAttDefBase,
  direction: PathDirection.parent,
  objectTypeId: typeName,
}

type CustomerToCustomerRuleMatrix = {
  [key in CustomerAccountType]: {
    [key in CustomerAccountType]: boolean
  }
}

class Customer extends Organization implements ICustomer {
  constructor({ accountType, type, ...ob }: CustomerProps) {
    super({ ...ob, type: type || typeName })

    /** In the before times, customers had an account type that dictated what kinds of customers they could have underneath them and various abilities
     * e.g. ability to have users, add gateways, have devices (in a whole inventory system that is also no longer used)
     * 5 Jnue 2025 ADH - There are some old deprecated methods that do still rely on accountType (addCustomer and addGateway (I know they sound important but they actually aren't used anymore)). They now won't work unless accountType is explicitly set
     */
    if (accountType) {
      this.accountType = accountType
    }
    if (this.canHaveSubCustomerTypeX()) {
      this.conditionals.Customer_SubCustomer_typeX = customerEdgeDefs.Customer_out_customer_has_subCustomer_typeX_Customer
    }
    if (this.canHaveSubCustomerTypeY()) {
      this.conditionals.Customer_SubCustomer_typeY = customerEdgeDefs.Customer_out_customer_has_subCustomer_typeY_Customer
    }
    if (this.canHaveSubCustomerTypeZ()) {
      this.conditionals.Customer_SubCustomer_typeZ = customerEdgeDefs.Customer_out_customer_has_subCustomer_typeZ_Customer
    }
    if (this.canHaveParentCustomerTypeV()) {
      this.conditionals.Customer_ParentCustomer_typeV = customerEdgeDefs.Customer_in_customer_has_subCustomer_typeV_Customer
    }
    if (this.canHaveParentCustomerTypeX()) {
      this.conditionals.Customer_ParentCustomer_typeX = customerEdgeDefs.Customer_in_customer_has_subCustomer_typeX_Customer
    }
    if (this.canHaveParentCustomerTypeY()) {
      this.conditionals.Customer_ParentCustomer_typeY = customerEdgeDefs.Customer_in_customer_has_subCustomer_typeY_Customer
    }
    if (this.canDefineServices()) {
      this.conditionals.ServiceOffering_define = {
        ...commonAttDefsIn,
        edgeTypeId: 'customer_defines_serviceOffering',
        //objectTypeId: 'ServiceOfferingDefinition',
        label: 'defines Service Offerings',
      }
    }
    if (this.canOfferServices()) {
      this.conditionals.ServiceOffering_offer = {
        ...commonAttDefsIn,
        edgeTypeId: 'customer_canOffer_serviceOffering',
        editable: false,
        //objectTypeId: 'ServiceOfferingDefinition',
        label: 'offers Service Offerings',
      }
    }
    if (this.canConsumeServices()) {
      this.conditionals.ServiceOffering_consume = {
        ...commonAttDefsIn,
        edgeTypeId: 'customer_canConsume_serviceOffering',
        editable: false,
        // objectTypeId: 'ServiceOfferingInstance',
        label: 'consumes Service Offerings',
      }
    }
    if (this.canHaveUsers()) {
      this.conditionals.Users = customerEdgeDefs.Customer_out_customer_has_users_User
    }
    if (this.canHaveDeviceInventory()) {
      this.conditionals.DeviceInventory = customerEdgeDefs.Customer_out_customer_device_inventory_includes_Device
    }
    if (this.canBeAllocatedDevices()) {
      this.conditionals.DeviceAllocation = customerEdgeDefs.Customer_out_customer_has_been_allocated_device_Device
    }
    if (this.canManageGateways()) {
      this.conditionals.DeviceAllocation = customerEdgeDefs.Customer_out_customer_manages_gateway_Gateway
    }
    if (this.canBeAllocatedGateways()) {
      this.conditionals.Gatewayllocation = customerEdgeDefs.Customer_out_customer_has_been_allocated_gateways_Gateway
    }
  }

  /** @deprecated - Customer account types no longer used */
  accountType?: CustomerAccountType
  /** @deprecated - Customer account types no longer used */
  @enumerable(false) canHaveSubCustomerTypeX() {
    if (!this.accountType) return false
    return [CustomerAccountType.V, CustomerAccountType.X].includes(this.accountType)
  }
  /** @deprecated - Customer account types no longer used */
  @enumerable(false) canHaveSubCustomerTypeY() {
    if (!this.accountType) return false
    return [CustomerAccountType.V, CustomerAccountType.X, CustomerAccountType.Y].includes(this.accountType)
  }
  /** @deprecated - Customer account types no longer used */
  @enumerable(false) canHaveSubCustomerTypeZ() {
    if (!this.accountType) return false
    return [CustomerAccountType.Y].includes(this.accountType)
  }

  /** @deprecated - Customer account types no longer used */
  @enumerable(false) canHaveParentCustomerTypeV() {
    if (!this.accountType) return false
    return [CustomerAccountType.X, CustomerAccountType.Y].includes(this.accountType)
  }
  /** @deprecated - Customer account types no longer used */
  @enumerable(false) canHaveParentCustomerTypeX() {
    if (!this.accountType) return false
    return [CustomerAccountType.X, CustomerAccountType.Y].includes(this.accountType)
  }
  /** @deprecated - Customer account types no longer used */
  @enumerable(false) canHaveParentCustomerTypeY() {
    if (!this.accountType) return false
    return [CustomerAccountType.Z].includes(this.accountType)
  }

  /** @deprecated - Customer account types no longer used */
  @enumerable(false) canDefineServices() {
    if (!this.accountType) return false
    return [CustomerAccountType.V].includes(this.accountType)
  }
  /** @deprecated - Customer account types no longer used */
  @enumerable(false) canOfferServices() {
    if (!this.accountType) return false
    return [CustomerAccountType.X, CustomerAccountType.Y].includes(this.accountType)
  }
  /** @deprecated - Customer account types no longer used */
  @enumerable(false) canConsumeServices() {
    if (!this.accountType) return false
    return [CustomerAccountType.Y, CustomerAccountType.Z].includes(this.accountType)
  }
  /** @deprecated - Customer account types no longer used */
  @enumerable(false) canHaveUsers() {
    if (!this.accountType) return false
    return [CustomerAccountType.V, CustomerAccountType.X, CustomerAccountType.Y, CustomerAccountType.Z].includes(this.accountType)
  }

  /** @deprecated - Customer account types no longer used */
  @enumerable(false) canHaveDeviceInventory() {
    if (!this.accountType) return false
    return [CustomerAccountType.V].includes(this.accountType)
  }
  /** @deprecated - Customer account types no longer used */
  @enumerable(false) canBeAllocatedDevices() {
    if (!this.accountType) return false
    return [CustomerAccountType.X, CustomerAccountType.Y, CustomerAccountType.Z].includes(this.accountType)
  }
  /** @deprecated - Customer account types no longer used */
  @enumerable(false) canManageGateways() {
    if (!this.accountType) return false
    return [CustomerAccountType.V, CustomerAccountType.X, CustomerAccountType.Y].includes(this.accountType)
  }
  /** @deprecated - Customer account types no longer used */
  @enumerable(false) canBeAllocatedGateways() {
    if (!this.accountType) return false
    return [CustomerAccountType.V, CustomerAccountType.X, CustomerAccountType.Y].includes(this.accountType)
  }

  @enumerable(false) conditionals: UserAttributeDefs = {}

  /** @deprecated - Customer account types no longer used */
  @enumerable(false) canHaveSubCustomer(accountType: CustomerAccountType): [boolean, UserAttributeDef] {
    switch (accountType) {
      case CustomerAccountType.X:
        return [this.canHaveSubCustomerTypeX(), this.conditionals?.Customer_SubCustomer_typeX]
      case CustomerAccountType.Y:
        return [this.canHaveSubCustomerTypeY(), this.conditionals?.Customer_SubCustomer_typeY]
      case CustomerAccountType.Z:
        return [this.canHaveSubCustomerTypeZ(), this.conditionals?.Customer_SubCustomer_typeZ]
      default:
        return [false, undefined]
    }
  }

  /** @deprecated - Customer account types no longer used */
  @enumerable(false) canHaveParentCustomer(accountType: CustomerAccountType): [boolean, UserAttributeDef] {
    switch (accountType) {
      case CustomerAccountType.V:
        return [this.canHaveParentCustomerTypeV(), this.conditionals?.Customer_ParentCustomer_typeV]
      case CustomerAccountType.X:
        return [this.canHaveParentCustomerTypeX(), this.conditionals?.Customer_ParentCustomer_typeX]
      case CustomerAccountType.Y:
        return [this.canHaveParentCustomerTypeY(), this.conditionals?.Customer_ParentCustomer_typeY]
      default:
        return [false, undefined]
    }
  }

  /** @deprecated - Customer account types no longer used */
  @enumerable(false) canAllocateDeviceTo(accountType: CustomerAccountType) {
    if (!this.accountType)
      return {
        [CustomerAccountType.V]: true,
        [CustomerAccountType.X]: true,
        [CustomerAccountType.Y]: true,
        [CustomerAccountType.Z]: true,
      }

    const ruleMatrix: CustomerToCustomerRuleMatrix = {
      [CustomerAccountType.V]: {
        [CustomerAccountType.V]: false,
        [CustomerAccountType.X]: true,
        [CustomerAccountType.Y]: true,
        [CustomerAccountType.Z]: false,
      },
      [CustomerAccountType.X]: {
        [CustomerAccountType.V]: false,
        [CustomerAccountType.X]: true,
        [CustomerAccountType.Y]: true,
        [CustomerAccountType.Z]: false,
      },
      [CustomerAccountType.Y]: {
        [CustomerAccountType.V]: false,
        [CustomerAccountType.X]: false,
        [CustomerAccountType.Y]: true,
        [CustomerAccountType.Z]: true,
      },
      [CustomerAccountType.Z]: {
        [CustomerAccountType.V]: false,
        [CustomerAccountType.X]: false,
        [CustomerAccountType.Y]: false,
        [CustomerAccountType.Z]: false,
      },
    }
    return ruleMatrix[this.accountType][accountType]
  }

  /** @deprecated - Customer account types no longer used */
  @enumerable(false) canAllocateGatewayTo(accountType: CustomerAccountType) {
    return this.canAllocateDeviceTo(accountType)
  }

  getUserAttributes(): UserAttributeDefs {
    return {
      ...super.getUserAttributes(),

      /** DEPRECATED FROM UI */
      /*
      ...this.conditionals,
      accountType: {
        enumKv: CustomerAccountType,
        type: 'Enum',
        editable: false,
      },
      */
    }
  }
}

export { Customer }
